<script setup lang="ts">
import type { ProjectGalleryCard } from '~/types';
import { TransitionRoot } from '@headlessui/vue';
import { computed, ref } from 'vue';

export interface VProjectGalleryProps {
  cards: ProjectGalleryCard[];
}

const props = defineProps<VProjectGalleryProps>();

const {
  isOpen,
  currentItemIdx,
  isHelpOpen,
  modalImageIndexes,
  currentItem,
  items: modalItems,
  openGallery,
  next,
  prev,
  close,
  toggleHelp,
} = useGalleryModal();

const activeCard = computed(() => {
  return props.cards.find((card) => card.postId === activePostId.value) ?? null;
});

const activePostId = ref<string | null>(null);

function openProjectGallery(card: ProjectGalleryCard) {
  activePostId.value = card.postId;
  openGallery(card.gallery, 0);
}
</script>

<template>
  <div class="grid gap-6 mt-4 xs:grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
    <button
      v-for="card in cards"
      :key="card.postId"
      type="button"
      class="relative block w-full overflow-hidden transition duration-300 border cursor-pointer rounded-card dark:border-gray-700 focus:outline-none"
      :aria-label="card.title ?? card.client ?? 'Project gallery'"
      @click="openProjectGallery(card)"
    >
      <div class="relative overflow-hidden rounded-card group">
        <NuxtImg
          v-if="card.cover"
          :src="card.cover.id"
          :alt="card.cover.description ?? card.title ?? ''"
          width="687"
          height="458"
          preload
          class="object-cover transition duration-300 group-hover:scale-110"
          placeholder
        />
        <div
          v-else
          class="flex items-center justify-center w-full h-[458px] bg-gray-200 dark:bg-gray-800"
        >
          <TypographyTitle class="px-4 text-center">{{ card.title ?? card.client }}</TypographyTitle>
        </div>
        <div
          class="absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-white/75 opacity-0 hover:opacity-100 dark:bg-gray-900/75"
        >
          <div class="p-8">
            <TypographyTitle v-if="card?.client">{{ card.client }}</TypographyTitle>
            <TypographyHeadline v-if="card?.title" :content="card.title" />
            <div v-if="card?.built_with" class="flex flex-wrap gap-2 mt-2">
              <UBadge v-for="(item, itemIdx) in card.built_with" :key="itemIdx" size="md" color="neutral">
                {{ item }}
              </UBadge>
            </div>
          </div>
        </div>
      </div>
    </button>
  </div>

  <!-- Gallery Modal -->
  <TransitionRoot
    :show="isOpen"
    enter="ease-out duration-300"
    enter-from="opacity-0"
    enter-to="opacity-100"
    leave="ease-in duration-200"
    leave-from="opacity-100"
    leave-to="opacity-0"
  >
    <div class="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-gray-900/75">
      <div class="relative flex flex-col items-center justify-center w-full h-full max-w-7xl text-white">
        <!-- Help Button -->
        <div
          v-auto-animate
          class="absolute z-50 hidden gap-2 bg-opacity-75 item-center md:flex top-4 left-4"
        >
          <div>
            <UButton
              :icon="isHelpOpen ? 'material-symbols:hide' : 'material-symbols:help-outline'"
              class="text-white"
              size="xl"
              @click="toggleHelp"
            />
          </div>

          <div
            v-if="isHelpOpen"
            class="flex items-center px-3 py-1 text-xs bg-gray-900 backdrop-blur-sm rounded-button gap-x-4"
          >
            <p>
              Press
              <UKbd value="esc" class="dark:text-white text-black"/>
              to close
            </p>
            <p>
              Press
              <UKbd value="←" class="dark:text-white text-black"/>
              or
              <UKbd value="→" class="dark:text-white text-black"/>
              to navigate
            </p>
          </div>
        </div>
        <!-- Close Button -->
        <UButton class="absolute z-50 top-4 right-4 text-white" icon="material-symbols:close-rounded" size="xl" @click="close" />

        <div class="flex items-center justify-center w-full h-full">
          <!-- Nav Buttons -->
          <UButton class="absolute z-50 left-4 text-white" icon="material-symbols:arrow-back-rounded" size="xl" @click="prev" />
          <UButton
            class="absolute z-50 right-4 text-white"
            icon="material-symbols:arrow-forward-rounded"
            size="xl"
            @click="next"
          />

          <!-- Image Area -->
          <div class="relative flex flex-col items-center justify-center w-full h-full p-20">
            <p
              v-if="activeCard?.title || activeCard?.client"
              class="inline-block px-6 py-2 text-sm text-white bg-gray-900 rounded-t-xl"
            >
              {{ activeCard?.client ?? activeCard?.title }}
            </p>
            <p
              v-if="currentItem?.description"
              class="inline-block px-6 py-2 text-sm text-white bg-gray-900 rounded-t-xl"
            >
              {{ currentItem.description }}
            </p>

            <template v-for="(item, idx) in modalItems" :key="item.id">
              <NuxtImg
                v-if="modalImageIndexes.includes(idx)"
                v-show="currentItemIdx === idx"
                :src="item.id"
                :alt="item.description ?? ''"
                :preload="currentItemIdx === idx"
                :loading="currentItemIdx === idx ? 'eager' : 'lazy'"
                height="800"
                class="object-contain w-full rounded-card max-h-[800px]"
                placeholder-class="bg-grey-500 animate-pulse"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </TransitionRoot>
</template>
